<template>
	<BaseTextField
		ref="textField"
		v-bind="$attrs"
		:value="displayValue"
		:label-text="labelText"
		:helper-text="helperText"
		:invalid="invalid"
		:icon="currencyCode"
		:disabled="disabled"
		type="text"
		:inputmode="inputDecimalPlaces === 0 ? 'numeric' : 'decimal'"
		@input="handleInput"
		@blur="handleBlur"
		@focus="handleFocus"
		@keypress="handleKeyPress"
		@keydown="$emit('keydown', $event)"
	/>
</template>

<script>
import { getCurrencyFractionDigits, truncateCurrencyValue } from '@/utils/currency'

export default {
	name: 'BaseCurrencyField',
	inheritAttrs: false,
	props: {
		value: {
			type: [Number, String],
			default: null
		},
		currency: {
			type: String,
			default: null
		},
		labelText: {
			type: String,
			default: ''
		},
		helperText: {
			type: String,
			default: null
		},
		invalid: {
			type: [Boolean, String],
			default: false
		},
		disabled: {
			type: [Boolean, String],
			default: false
		},
		min: {
			type: [Number, String],
			default: null
		},
		max: {
			type: [Number, String],
			default: null
		},
		allowDecimals: {
			type: Boolean,
			default: false
		}
	},
	data () {
		return {
			isFocused: false,
			internalValue: ''
		}
	},
	computed: {
		// currencySymbol () {
		// 	return this.$options.filters.currencySymbol(this.currencyCode)
		// },
		currencyCode () {
			return this.currency || this.$auth().currency
		},
		decimalPlaces () {
			return getCurrencyFractionDigits(
				this.currencyCode,
				this.$i18n?.locale || 'en-US'
			)
		},
		inputDecimalPlaces () {
			return this.allowDecimals ? Math.max(this.decimalPlaces, 2) : this.decimalPlaces
		},
		displayValue () {
			if (this.isFocused) {
				return this.internalValue
			}

			if (this.value === null || this.value === '' || this.value === undefined) {
				return ''
			}

			const numericValue = parseFloat(this.value)
			if (isNaN(numericValue)) {
				return ''
			}

			return this.$options.filters.decimal(numericValue, this.currencyCode, this.inputDecimalPlaces)
		}
	},
	watch: {
		value: {
			immediate: true,
			handler (newValue) {
				if (!this.isFocused && newValue !== null && newValue !== '') {
					this.internalValue = this.editableValue(newValue)
				}
			}
		}
	},
	methods: {
		editableValue (value) {
			if (value === null || value === '' || value === undefined) {
				return ''
			}
			if (this.inputDecimalPlaces === 0) {
				const numericValue = parseFloat(String(value).replace(',', '.'))
				return isNaN(numericValue) ? '' : String(Math.trunc(numericValue))
			}
			return String(value)
		},
		parseFormattedValue (formattedValue) {
			if (!formattedValue) return null
			const parsed = parseFloat(formattedValue.replace(/[^0-9.,]/g, '').replace(',', '.'))
			return isNaN(parsed) ? null : parsed
		},
		handleKeyPress (event) {
			const key = event.key
			const currentValue = this.internalValue || ''

			// Allow control keys
			if (event.ctrlKey || event.metaKey) {
				return
			}

			// Allow navigation and editing keys
			if (['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'Home', 'End', 'ArrowLeft', 'ArrowRight'].includes(key)) {
				return
			}

			// For zero-decimal currencies (JPY, KRW), only allow digits
			if (this.inputDecimalPlaces === 0) {
				if (!/^\d$/.test(key)) {
					event.preventDefault()
				}
				return
			}

			// Allow decimal separator (. or ,) only once
			if (['.', ','].includes(key)) {
				if (currentValue.includes('.') || currentValue.includes(',')) {
					event.preventDefault()
				}
				return
			}

			// Only allow digits
			if (!/^\d$/.test(key)) {
				event.preventDefault()
				return
			}

			// Prevent entering more decimals than allowed
			const separatorMatch = currentValue.match(/[.,]/)
			if (separatorMatch) {
				const separatorIndex = currentValue.indexOf(separatorMatch[0])
				const decimalPart = currentValue.substring(separatorIndex + 1)
				const cursorPosition = event.target.selectionStart

				if (cursorPosition > separatorIndex && decimalPart.length >= this.inputDecimalPlaces) {
					event.preventDefault()
				}
			}
		},
		handleFocus (event) {
			this.isFocused = true
			this.internalValue = this.editableValue(this.value)
			this.$emit('focus', event)
		},
		handleInput (value) {
			let sanitized = value

			if (this.inputDecimalPlaces === 0) {
				// Integer-only currencies
				const integerOnly = value.replace(/[^0-9.,]/g, '')
				const separatorIndex = integerOnly.search(/[.,]/)
				sanitized = separatorIndex === -1 ? integerOnly : integerOnly.slice(0, separatorIndex)
			} else {
				// Decimal currencies
				sanitized = value.replace(/[^0-9.,]/g, '')

				const separatorMatch = sanitized.match(/[.,]/)
				if (separatorMatch) {
					const separator = separatorMatch[0]
					const parts = sanitized.split(separator)
					const integerPart = parts[0]
					let decimalPart = parts.slice(1).join('').replace(/[.,]/g, '')

					if (decimalPart.length > this.inputDecimalPlaces) {
						decimalPart = decimalPart.slice(0, this.inputDecimalPlaces)
					}

					sanitized = decimalPart ? `${integerPart}${separator}${decimalPart}` : `${integerPart}${separator}`
				}
			}

			this.internalValue = sanitized

			// Emit numeric value on every input change
			const numericValue = this.parseFormattedValue(sanitized)
			this.$emit('input', numericValue)
		},
		handleBlur (event) {
			this.isFocused = false
			const numericValue = this.parseFormattedValue(this.internalValue)

			if (numericValue !== null) {
				const normalized = truncateCurrencyValue(numericValue, this.inputDecimalPlaces)
				const minVal = this.min !== null ? parseFloat(this.min) : null
				const maxVal = this.max !== null ? parseFloat(this.max) : null
				const clamped = minVal !== null && normalized < minVal ? minVal
					: maxVal !== null && normalized > maxVal ? maxVal
						: normalized

				if (clamped !== numericValue || clamped !== parseFloat(this.value)) {
					this.$emit('input', clamped)
				}
			} else if (this.internalValue === '') {
				this.$emit('input', null)
			}

			this.$emit('blur', event, numericValue)
		}
	}
}
</script>
